import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  FormHelperText
} from '@mui/material';
import { useSnackbar } from 'notistack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkIcon from '@mui/icons-material/Link';
import ArticleIcon from '@mui/icons-material/Article';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { fetchResources, deleteResource, getResourceDownloadUrl, createResource, updateResource } from '../services/api';

const RESOURCE_CATEGORIES = [
  "CV Templates",
  "Cover Letter Templates",
  "Study Guides",
  "Scholarship Forms",
  "Application Tips",
  "Interview Prep",
  "Other"
];

const ResourceManager = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentResource, setCurrentResource] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    type: 'file',
    file: null,
    youtube_url: '',
    external_url: ''
  });
  const [errors, setErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState('');

  const loadResources = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchResources();
      setResources(data);
    } catch (error) {
      enqueueSnackbar('Failed to load resources', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
    resetForm();
  };

  const handleEdit = (resource) => {
    setCurrentResource(resource);
    setFormData({
      title: resource.title,
      description: resource.description,
      category: resource.category,
      type: resource.type,
      file: null, // Can't pre-populate file
      youtube_url: resource.youtube_url || '',
      external_url: resource.external_url || ''
    });
    setEditMode(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      type: 'file',
      file: null,
      youtube_url: '',
      external_url: ''
    });
    setErrors({});
    setPreviewUrl('');
    setCurrentResource(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({
        ...formData,
        file
      });

      // Create a preview URL for images
      if (file.type.startsWith('image/')) {
        const fileUrl = URL.createObjectURL(file);
        setPreviewUrl(fileUrl);
      } else {
        setPreviewUrl('');
      }

      // Clear error
      if (errors.file) {
        setErrors({
          ...errors,
          file: ''
        });
      }
    }
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData({
      ...formData,
      type: newType,
      // Reset fields not relevant to this type
      file: newType === 'file' ? formData.file : null,
      youtube_url: newType === 'youtube' ? formData.youtube_url : '',
      external_url: newType === 'link' ? formData.external_url : ''
    });
    setPreviewUrl('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.type === 'file' && !formData.file && !editMode) {
      newErrors.file = 'File is required';
    }

    if (formData.type === 'youtube' && !formData.youtube_url.trim()) {
      newErrors.youtube_url = 'YouTube URL is required';
    } else if (formData.type === 'youtube' && !isValidYouTubeUrl(formData.youtube_url)) {
      newErrors.youtube_url = 'Invalid YouTube URL';
    }

    if (formData.type === 'link' && !formData.external_url.trim()) {
      newErrors.external_url = 'URL is required';
    } else if (formData.type === 'link' && !isValidUrl(formData.external_url)) {
      newErrors.external_url = 'Invalid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidYouTubeUrl = (url) => {
    // Basic YouTube URL validation
    const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return pattern.test(url);
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const formDataToSend = new FormData();

      // Add all necessary fields
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('type', formData.type);

      // Add conditional fields based on type
      if (formData.type === 'youtube') {
        formDataToSend.append('youtube_url', formData.youtube_url || '');
      }

      if (formData.type === 'link') {
        formDataToSend.append('external_url', formData.external_url || '');
      }

      if (formData.type === 'file' && formData.file) {
        formDataToSend.append('file', formData.file);
      }

      // Log what we're sending
      console.log('Form data being sent:');
      for (let pair of formDataToSend.entries()) {
        console.log(pair[0] + ': ' + (pair[0] === 'file' ? pair[1].name : pair[1]));
      }

      // Use the api service instead of raw XHR
      const result = editMode && currentResource
        ? await updateResource(currentResource._id, formDataToSend)
        : await createResource(formDataToSend);

      console.log('Response:', result);

      enqueueSnackbar(editMode ? 'Resource updated successfully' : 'Resource created successfully', {
        variant: 'success'
      });

      handleClose();
      loadResources();
    } catch (error) {
      console.error('Error saving resource:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      enqueueSnackbar(`Error saving resource: ${errorMessage}`, { variant: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      try {
        await deleteResource(id);
        enqueueSnackbar('Resource deleted successfully', { variant: 'success' });
        loadResources();
      } catch (error) {
        enqueueSnackbar('Error deleting resource', { variant: 'error' });
      }
    }
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return <ArticleIcon />;

    if (fileType.includes('pdf')) {
      return <PictureAsPdfIcon />;
    } else if (fileType.includes('word') || fileType.includes('doc')) {
      return <ArticleIcon />;
    } else if (fileType.includes('image')) {
      return <ArticleIcon />;
    }

    return <ArticleIcon />;
  };

  const getTypeChip = (resource) => {
    switch (resource.type) {
      case 'file':
        return (
          <Chip
            icon={getFileIcon(resource.file_type)}
            label="File"
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      case 'youtube':
        return (
          <Chip
            icon={<YouTubeIcon />}
            label="YouTube"
            size="small"
            color="error"
            variant="outlined"
          />
        );
      case 'link':
        return (
          <Chip
            icon={<LinkIcon />}
            label="Link"
            size="small"
            color="info"
            variant="outlined"
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 500 }}>
          Resource Management
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpen}
        >
          Add Resource
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resources.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" sx={{ my: 3 }}>
                      No resources found. Click "Add Resource" to create your first resource.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                resources.map((resource) => (
                  <TableRow key={resource._id} hover>
                    <TableCell>{resource.title}</TableCell>
                    <TableCell>{getTypeChip(resource)}</TableCell>
                    <TableCell>{resource.category}</TableCell>
                    <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {resource.description}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {resource.type === 'file' && (
                          <Tooltip title="Download">
                            <IconButton
                              color="primary"
                              size="small"
                              href={getResourceDownloadUrl(resource._id)}
                              target="_blank"
                            >
                              <FileDownloadIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {resource.type === 'youtube' && (
                          <Tooltip title="View on YouTube">
                            <IconButton
                              color="error"
                              size="small"
                              href={resource.youtube_url}
                              target="_blank"
                            >
                              <YouTubeIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {resource.type === 'link' && (
                          <Tooltip title="Open link">
                            <IconButton
                              color="info"
                              size="small"
                              href={resource.external_url}
                              target="_blank"
                            >
                              <LinkIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => handleEdit(resource)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDelete(resource._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Resource Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Resource' : 'Add New Resource'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label="Title"
                fullWidth
                value={formData.title}
                onChange={handleInputChange}
                error={!!errors.title}
                helperText={errors.title}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  label="Category"
                >
                  {RESOURCE_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={editMode}>
                <InputLabel>Type</InputLabel>
                <Select
                  name="type"
                  value={formData.type}
                  onChange={handleTypeChange}
                  label="Type"
                  disabled={editMode} // Can't change type after creation
                >
                  <MenuItem value="file">File Upload</MenuItem>
                  <MenuItem value="youtube">YouTube Video</MenuItem>
                  <MenuItem value="link">External Link</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                error={!!errors.description}
                helperText={errors.description}
              />
            </Grid>

            {/* Conditional fields based on type */}
            {formData.type === 'file' && (
              <Grid item xs={12}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 1 }}
                >
                  {editMode ? 'Replace File (Optional)' : 'Upload File'}
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
                {formData.file && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected: {formData.file.name}
                  </Typography>
                )}
                {previewUrl && (
                  <Box sx={{ mt: 2, maxWidth: 300 }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      style={{ maxWidth: '100%', maxHeight: 200 }}
                    />
                  </Box>
                )}
                {errors.file && (
                  <FormHelperText error>{errors.file}</FormHelperText>
                )}
                {editMode && !formData.file && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Current file: {currentResource?.file_name || 'No file uploaded'}
                  </Typography>
                )}
              </Grid>
            )}

            {formData.type === 'youtube' && (
              <Grid item xs={12}>
                <TextField
                  name="youtube_url"
                  label="YouTube URL"
                  fullWidth
                  value={formData.youtube_url}
                  onChange={handleInputChange}
                  error={!!errors.youtube_url}
                  helperText={errors.youtube_url || "Example: https://www.youtube.com/watch?v=EXAMPLE"}
                  InputProps={{
                    startAdornment: <YouTubeIcon sx={{ mr: 1, color: 'error.main' }} />,
                  }}
                />
              </Grid>
            )}

            {formData.type === 'link' && (
              <Grid item xs={12}>
                <TextField
                  name="external_url"
                  label="External URL"
                  fullWidth
                  value={formData.external_url}
                  onChange={handleInputChange}
                  error={!!errors.external_url}
                  helperText={errors.external_url || "Example: https://example.com"}
                  InputProps={{
                    startAdornment: <LinkIcon sx={{ mr: 1, color: 'info.main' }} />,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
          >
            {editMode ? 'Update Resource' : 'Save Resource'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResourceManager; 